import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreateVendor, UpdateVendor, Vendor } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['vendors', companyId] as const;

export function useVendors(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () => api<Vendor[]>(`/companies/${companyId}/vendors?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useVendor(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['vendors', companyId, id],
    queryFn: () => api<Vendor>(`/companies/${companyId}/vendors/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Resolve a vendor by 13-digit Thai tax ID (GET vendors/by-tax-id/:taxId).
// Returns 404 when no vendor matches — that is an EXPECTED "no match" signal for
// the review screen, not a hard error, so retry is disabled and the caller reads
// isError to drive the manual-pick fallback. Disabled until a taxId is present.
export function useVendorByTaxId(companyId: string | undefined, taxId: string | undefined | null) {
  return useQuery({
    queryKey: ['vendors', companyId, 'by-tax-id', taxId],
    queryFn: () => api<Vendor>(`/companies/${companyId}/vendors/by-tax-id/${taxId}`),
    enabled: !!companyId && !!taxId,
    retry: false,
  });
}

export function useCreateVendor(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateVendor, 'companyId'>) =>
      apiJson<typeof input, Vendor>('POST', `/companies/${companyId}/vendors`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateVendor(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVendor }) =>
      apiJson<UpdateVendor, Vendor>('PATCH', `/companies/${companyId}/vendors/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteVendor(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, Vendor>('DELETE', `/companies/${companyId}/vendors/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
